
import React, { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  SelectChangeEvent,
  FormHelperText,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import Accordion from "react-bootstrap/Accordion";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";

// ─── Types ────────────────────────────────────────────────────────────────────
interface OptionType {
  value: string;
  label: string;
}

interface TouchedType {
  [key: string]: boolean;
}

interface ErrorsType {
  [key: string]: string;
}

interface CostObjectFormData {
  type: string;
  costType: string;
  amount: string;
  currency: string;
  dueDate: string;
  book: string;
  trader: string;
  strategy: string;
  clearer: string;
  broker: string;
  product: string;
  counterparty: string;
  requiresSettlement: string;
  excludeFromPNL: string;
  description: string;
  status: string;
}

// ─── Mock dropdown data — replace with API calls when ready ──────────────────
const MOCK_BOOKS: OptionType[] = [
  { value: "MM", label: "MM" },
  { value: "GAS", label: "GAS" },
  { value: "POWER", label: "POWER" },
  { value: "OIL", label: "OIL" },
];
const MOCK_TRADERS: OptionType[] = [
  { value: "developer", label: "Developer" },
  { value: "tester", label: "Tester" },
  { value: "director", label: "Director" },
  { value: "manager", label: "Manager" },
];
const MOCK_STRATEGIES: OptionType[] = [
  { value: "1000", label: "1000" },
  { value: "1001", label: "1001" },
  { value: "2000", label: "2000" },
];
const MOCK_CLEARERS: OptionType[] = [
  { value: "RJO", label: "RJO" },
  { value: "ICE", label: "ICE" },
  { value: "CME", label: "CME" },
];
const MOCK_BROKERS: OptionType[] = [
  { value: "BGM", label: "BGM" },
  { value: "GFI", label: "GFI" },
  { value: "MAREX", label: "Marex" },
];
const MOCK_PRODUCTS: OptionType[] = [
  { value: "brent", label: "Brent" },
  { value: "wti", label: "WTI" },
  { value: "gasoil", label: "Gasoil" },
  { value: "naphtha", label: "Naphtha" },
];
const MOCK_COUNTERPARTIES: OptionType[] = [
  { value: "BGM", label: "BGM" },
  { value: "GFI", label: "GFI" },
  { value: "VITOL", label: "Vitol" },
  { value: "SHELL", label: "Shell" },
];
const MOCK_CURRENCIES: OptionType[] = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "AED", label: "AED" },
];
const COST_TYPES: OptionType[] = [
  { value: "risk_reserve", label: "Risk Reserve" },
  { value: "brokerage_adjustment", label: "Brokerage Adjustment" },
  { value: "fee", label: "Fee" },
  { value: "margin", label: "Margin" },
  { value: "other", label: "Other" },
];
const STATUSES: OptionType[] = [
  { value: "pending", label: "Pending" },
  { value: "committed", label: "Committed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

// ─── Shared compact field styles ─────────────────────────────────────────────
const compactFieldSx = {
  flex: {
    xs: "1 1 auto",
    sm: "1 1 25%",
    md: "1 1 25%",
    lg: "1 1 25%",
    xl: "1 1 12%",
  },
  "& .MuiInputBase-root": {
    fontSize: "0.7rem",
    height: "32px",
    fontWeight: "bold",
    marginBottom: "0.5rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.9rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -8px) scale(0.74)",
      backgroundColor: "white",
      paddingRight: "0.4rem",
      fontWeight: "bold",
      color: "black",
      letterSpacing: "normal",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b9baeb85",
    borderWidth: "2px",
  },
};

const compactFieldSxError = {
  ...compactFieldSx,
  "& .MuiInputLabel-root": {
    ...compactFieldSx["& .MuiInputLabel-root"],
    "&.MuiInputLabel-shrink": {
      ...(compactFieldSx["& .MuiInputLabel-root"] as any)["&.MuiInputLabel-shrink"],
      color: "#d32f2f",
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d32f2f",
    borderWidth: "2px",
  },
};

// ─── CompactSelect ────────────────────────────────────────────────────────────
const CompactSelect: React.FC<{
  label: string;
  options: OptionType[];
  value: string;
  onChange: (e: SelectChangeEvent) => void;
  disabled?: boolean;
  sx?: object;
  slotProps?: { inputLabel?: { shrink?: boolean } };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
}> = ({
  label, options, value, onChange, disabled = false,
  sx, slotProps, onBlur, error = false, helperText,
}) => {
    const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label));
    const dynamicLabelProps = value
      ? { shrink: true, ...slotProps?.inputLabel }
      : slotProps?.inputLabel || {};

    return (
      <FormControl
        fullWidth
        size="small"
        error={error}
        disabled={disabled}
        sx={{ ...(error ? compactFieldSxError : compactFieldSx), ...sx }}
      >
        <InputLabel {...dynamicLabelProps}>{label}</InputLabel>
        <Select
          value={value}
          label={label}
          onChange={onChange}
          onBlur={onBlur}
          displayEmpty
          sx={{ "& .MuiSelect-select": { py: 0.5, color: value ? "inherit" : "#999" } }}
        >
          {!value && (
            <MenuItem value="" disabled sx={{ fontSize: "0.7rem", color: "#999" }}>
              Select
            </MenuItem>
          )}
          {sortedOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.7rem" }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <FormHelperText sx={{ fontSize: "0.65rem", margin: "3px 0 0 0" }}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  };

// ─── CompactTextField ─────────────────────────────────────────────────────────
const CompactTextField: React.FC<{
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  disabled?: boolean;
  sx?: object;
  slotProps?: { inputLabel?: { shrink?: boolean } };
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}> = ({
  label, value, onChange, type = "text", disabled = false,
  sx, slotProps, error = false, helperText, onBlur,
  multiline = false, rows = 1, placeholder,
}) => {
    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (
        type === "number" &&
        (event.key === "e" || event.key === "E" || event.key === "-" || event.key === "+")
      ) {
        event.preventDefault();
      }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      if (type === "number") {
        if (/^\d*\.?\d*$/.test(inputValue) || inputValue === "") {
          onChange(event);
        }
      } else {
        onChange(event);
      }
    };

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        error={error}
        helperText={helperText}
        type={type}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        placeholder={placeholder}
        InputLabelProps={{
          shrink: type === "date" ? true : slotProps?.inputLabel?.shrink,
        }}
        InputProps={{
          onKeyPress: handleKeyPress,
          inputMode: type === "number" ? "numeric" : undefined,
          sx: { fontSize: "0.7rem", height: multiline ? "auto" : "32px" },
        }}
        sx={{
          ...(error ? compactFieldSxError : compactFieldSx),
          "& .MuiInputBase-root": {
            fontSize: "0.7rem",
            height: multiline ? "auto" : "32px",
            fontWeight: "bold",
            marginBottom: "0.5rem",
          },
          ...sx,
        }}
      />
    );
  };

// ─── Edit Form Component ──────────────────────────────────────────────────────
interface CostObjectEditProps {
  dataVersion: number;
  setDataVersion: React.Dispatch<React.SetStateAction<number>>;
  editedData: any;
  isEditMode: boolean;
  setEditMode: (v: boolean) => void;
}

const CostObjectEdit: React.FC<CostObjectEditProps> = ({
  dataVersion,
  setDataVersion,
  editedData,
  setEditMode,
}) => {
  const [isFormModified, setIsFormModified] = useState(false);
  const [touched, setTouched] = useState<TouchedType>({});
  const [errors, setErrors] = useState<ErrorsType>({});

  // Dropdown options — swap mock arrays with API calls when ready
  const [bookOptions] = useState<OptionType[]>(MOCK_BOOKS);
  const [traderOptions] = useState<OptionType[]>(MOCK_TRADERS);
  const [strategyOptions] = useState<OptionType[]>(MOCK_STRATEGIES);
  const [clearerOptions] = useState<OptionType[]>(MOCK_CLEARERS);
  const [brokerOptions] = useState<OptionType[]>(MOCK_BROKERS);
  const [productOptions] = useState<OptionType[]>(MOCK_PRODUCTS);
  const [counterpartyOptions] = useState<OptionType[]>(MOCK_COUNTERPARTIES);
  const [currencyOptions] = useState<OptionType[]>(MOCK_CURRENCIES);
  const [costTypeOptions] = useState<OptionType[]>(COST_TYPES);

  const [formData, setFormData] = useState<CostObjectFormData>({
    type: "lump_sum",
    costType: "",
    amount: "",
    currency: "USD",
    dueDate: "",
    book: "",
    trader: "",
    strategy: "",
    clearer: "",
    broker: "",
    product: "",
    counterparty: "",
    requiresSettlement: "yes",
    excludeFromPNL: "no",
    description: "",
    status: "pending",
  });

  // TODO: Replace mock data with API calls when endpoints are ready.
  // Example pattern (same as contract page):
  // useEffect(() => {
  //   fetchBookOptions();
  //   fetchTraderOptions();
  //   ...
  // }, [dataVersion]);

  // Populate from editedData (same pattern as CompactFormEdit)
  useEffect(() => {
    console.log("useEffect editedData", editedData);
    if (!editedData) return;

    setFormData({
      type: editedData.type || "lump_sum",
      costType: editedData.cost_type || "",
      amount: editedData.amount != null ? String(editedData.amount) : "",
      currency: editedData.currency || "USD",
      dueDate: editedData.due_date || "",
      book: editedData.book || "",
      trader: editedData.trader || "",
      strategy: editedData.strategy || "",
      clearer: editedData.clearer || "",
      broker: editedData.broker || "",
      product: editedData.product || "",
      counterparty: editedData.counterparty || "",
      requiresSettlement: editedData.requires_settlement === false ? "no" : "yes",
      excludeFromPNL: editedData.exclude_from_pnl === true ? "yes" : "no",
      description: editedData.description || "",
      status: editedData.status || "pending",
    });

    setTouched({});
    setErrors({});
  }, [editedData]);

  // ─── Validation ─────────────────────────────────────────────────────────────
  const requiredFields: (keyof CostObjectFormData)[] = [
    "costType", "amount", "currency", "book", "trader",
    "strategy", "clearer", "broker", "product", "counterparty",
  ];

  const fieldLabels: Record<string, string> = {
    costType: "Cost Type",
    amount: "Amount",
    currency: "Currency",
    book: "Book",
    trader: "Trader",
    strategy: "Strategy",
    clearer: "Clearer",
    broker: "Broker",
    product: "Product",
    counterparty: "Counterparty",
  };

  const validateField = (field: keyof CostObjectFormData, value: string): string => {
    if (requiredFields.includes(field) && (!value || value.trim() === "")) {
      return `${fieldLabels[field as string] || field} is required`;
    }
    if (field === "amount" && value && isNaN(Number(value))) {
      return "Amount must be a valid number";
    }
    if (field === "amount" && value && Number(value) === 0) {
      return "Amount cannot be zero";
    }
    return "";
  };

  const validateAll = (): boolean => {
    const newErrors: ErrorsType = {};
    let valid = true;
    requiredFields.forEach((f) => {
      const err = validateField(f, formData[f]);
      if (err) {
        newErrors[f as string] = err;
        valid = false;
      }
    });
    setErrors(newErrors);
    return valid;
  };

  const handleBlur = (field: keyof CostObjectFormData) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const handleSelectChange =
    (field: keyof CostObjectFormData) => (e: SelectChangeEvent) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      setIsFormModified(true);
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const handleTextChange =
    (field: keyof CostObjectFormData) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      setIsFormModified(true);
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const handleRadioChange =
    (field: keyof CostObjectFormData) => (e: ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
      setIsFormModified(true);
    };

  const resetFormData = () => {
    setFormData({
      type: "lump_sum",
      costType: "",
      amount: "",
      currency: "USD",
      dueDate: "",
      book: "",
      trader: "",
      strategy: "",
      clearer: "",
      broker: "",
      product: "",
      counterparty: "",
      requiresSettlement: "yes",
      excludeFromPNL: "no",
      description: "",
      status: "pending",
    });
    setTouched({});
    setErrors({});
    setIsFormModified(false);
  };

  const SubmitFormdata = (e: React.FormEvent) => {
    e.preventDefault();

    const allTouched: TouchedType = {};
    requiredFields.forEach((f) => {
      allTouched[f as string] = true;
    });
    setTouched(allTouched);

    if (!validateAll()) {
      toast.error("Please complete all required fields before submitting");
      return;
    }

    const payload = {
      id: editedData?.id,
      status: formData.status,
      type: formData.type,
      cost_type: formData.costType,
      amount: Number(formData.amount),
      currency: formData.currency,
      due_date: formData.dueDate || null,
      book: formData.book,
      trader: formData.trader,
      strategy: formData.strategy,
      clearer: formData.clearer,
      broker: formData.broker,
      product: formData.product,
      counterparty: formData.counterparty,
      requires_settlement: formData.requiresSettlement === "yes",
      exclude_from_pnl: formData.excludeFromPNL === "yes",
      description: formData.description || null,
      updated_at: new Date().toISOString(),
    };

    console.log("📦 Cost Object Update Payload:", payload);

    // TODO: Replace with real API call when endpoint is ready:
    // const contractApiSubmit = async () => {
    //   try {
    //     const response = await axiosInstance.put(
    //       `${backendbaseUrl}/paper-app/cost-objects/${editedData.id}/`,
    //       payload
    //     );
    //     if ([200, 201, 202, 203].includes(response.status)) {
    //       toast.success("✅ Cost Object updated successfully!");
    //       setDataVersion(dataVersion + 1);
    //       setEditMode(false);
    //       resetFormData();
    //     }
    //   } catch (error) {
    //     toast.error(`An error occurred: ${error}`);
    //   }
    // };
    // contractApiSubmit();

    // Mock store update (remove when API is live)
    try {
      const STORAGE_KEY = "cost_objects_mock_data";
      const existing: any[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const idx = existing.findIndex((item) => item.id === payload.id);
      if (idx !== -1) {
        existing[idx] = { ...existing[idx], ...payload };
      } else {
        existing.push(payload);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    } catch {
      /* ignore */
    }

    toast.success(`✅ Cost Object ${payload.id} updated successfully!`);
    setDataVersion((prev) => prev + 1);
    setEditMode(false);
    resetFormData();
  };

  return (
    <>
      <div className="trading-wrapper">
        <div className="trading-header d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-pencil-square fs-5 text-primary"></i>
            <span className="fs-5 fw-semibold text-dark">Update</span>
          </div>
        </div>

        <Accordion defaultActiveKey="0" className="table-ad__addInputs">
          <Accordion.Item eventKey="0">
            <Accordion.Header className="accordion_button">
              Edit Cost Object
              {editedData?.id && (
                <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "8px", fontWeight: "normal" }}>
                  — ID: {editedData.id}
                </span>
              )}
            </Accordion.Header>
            <Accordion.Body>
              <Box
                sx={{
                  width: "100%",
                  p: { xs: 1, sm: 1 },
                  pt: "20px !important",
                  backgroundColor: "#ffff",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              >
                {/* ── Row 1: Type, Cost Type, Amount, Currency, Due Date, Status ── */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    "& > *": {
                      flex: "1 1 0px",
                      minWidth: {
                        xs: "100%",
                        sm: "48%",
                        md: "23%",
                        lg: "15%",
                        xl: "7.5%",
                      },
                    },
                  }}
                >
                  {/* Type — always Lump Sum for Phase 1 */}
                  <CompactSelect
                    label="Type"
                    value={formData.type}
                    onChange={handleSelectChange("type")}
                    options={[{ value: "lump_sum", label: "Lump Sum" }]}
                    disabled
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <CompactSelect
                    label="Cost Type *"
                    value={formData.costType}
                    onChange={handleSelectChange("costType")}
                    options={costTypeOptions}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={touched.costType && !!errors.costType}
                    helperText={touched.costType ? errors.costType : ""}
                    onBlur={() => handleBlur("costType")}
                  />
                  <CompactTextField
                    label="Amount *"
                    value={formData.amount}
                    onChange={handleTextChange("amount")}
                    type="number"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={touched.amount && !!errors.amount}
                    helperText={touched.amount ? errors.amount : ""}
                    onBlur={() => handleBlur("amount")}
                  />
                  <CompactSelect
                    label="Currency *"
                    value={formData.currency}
                    onChange={handleSelectChange("currency")}
                    options={currencyOptions}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={touched.currency && !!errors.currency}
                    helperText={touched.currency ? errors.currency : ""}
                    onBlur={() => handleBlur("currency")}
                  />
                  <CompactTextField
                    label="Due Date"
                    value={formData.dueDate}
                    onChange={handleTextChange("dueDate")}
                    type="date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    onBlur={() => handleBlur("dueDate")}
                  />
                  <CompactSelect
                    label="Status"
                    value={formData.status}
                    onChange={handleSelectChange("status")}
                    options={STATUSES}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Box>

                {/* ── Row 2: Book, Trader, Strategy, Clearer, Broker, Product, Counterparty ── */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mt: 0.5,
                    "& > *": {
                      flex: "1 1 0px",
                      minWidth: {
                        xs: "100%",
                        sm: "48%",
                        md: "23%",
                        lg: "15%",
                        xl: "7.5%",
                      },
                    },
                  }}
                >
                  {[
                    { label: "Book", field: "book" as const, options: bookOptions },
                    { label: "Trader", field: "trader" as const, options: traderOptions },
                    { label: "Strategy", field: "strategy" as const, options: strategyOptions },
                    { label: "Clearer", field: "clearer" as const, options: clearerOptions },
                    { label: "Broker", field: "broker" as const, options: brokerOptions },
                    { label: "Product", field: "product" as const, options: productOptions },
                    { label: "Counterparty", field: "counterparty" as const, options: counterpartyOptions },
                  ].map(({ label, field, options }) => (
                    <CompactSelect
                      key={field}
                      label={`${label} *`}
                      value={formData[field]}
                      onChange={handleSelectChange(field)}
                      options={options}
                      slotProps={{ inputLabel: { shrink: true } }}
                      error={touched[field] && !!errors[field]}
                      helperText={touched[field] ? errors[field] : ""}
                      onBlur={() => handleBlur(field)}
                    />
                  ))}
                </Box>

                {/* ── Row 3: Requires Settlement, Exclude from PNL (radio groups) ── */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mt: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#333", mb: 0.25 }}
                    >
                      Requires Settlement
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formData.requiresSettlement}
                      onChange={handleRadioChange("requiresSettlement")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                        label={<span style={{ fontSize: "0.75rem" }}>Yes</span>}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                        label={<span style={{ fontSize: "0.75rem" }}>No</span>}
                      />
                    </RadioGroup>
                  </FormControl>

                  <FormControl component="fieldset">
                    <FormLabel
                      component="legend"
                      sx={{ fontSize: "0.75rem", fontWeight: "bold", color: "#333", mb: 0.25 }}
                    >
                      Exclude from PNL
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formData.excludeFromPNL}
                      onChange={handleRadioChange("excludeFromPNL")}
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                        label={<span style={{ fontSize: "0.75rem" }}>Yes</span>}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" sx={{ py: 0.25 }} />}
                        label={<span style={{ fontSize: "0.75rem" }}>No</span>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* ── Row 4: Description ── */}
                <Box sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={formData.description}
                    onChange={handleTextChange("description")}
                    multiline
                    rows={3}
                    placeholder="Enter description..."
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { fontSize: "0.7rem" } }}
                    sx={{
                      "& .MuiInputBase-root": { fontWeight: "bold", marginBottom: "0.5rem" },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.9rem",
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, -8px) scale(0.74)",
                          backgroundColor: "white",
                          paddingRight: "0.4rem",
                          fontWeight: "bold",
                          color: "black",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#b9baeb85",
                        borderWidth: "2px",
                      },
                    }}
                  />
                </Box>

                {/* ── Actions ── */}
                <Box sx={{ display: "flex", mt: 1, gap: 1, justifyContent: "flex-end" }}>
                  <Button
                    onClick={SubmitFormdata}
                    variant="contained"
                    disabled={!isFormModified}
                    sx={{
                      height: { xs: "36px", sm: "32px" },
                      flex: { xs: "1 1 auto", sm: "0 0 auto" },
                      minWidth: { xs: "100%", sm: "100px" },
                      maxWidth: { sm: "150px" },
                      backgroundColor: "#00bcd4",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      "&:hover": { backgroundColor: "#008c9e" },
                    }}
                  >
                    Update
                  </Button>
                  <Button
                    onClick={() => setEditMode(false)}
                    variant="outlined"
                    sx={{
                      height: { xs: "36px", sm: "32px" },
                      flex: { xs: "1 1 auto", sm: "0 0 auto" },
                      minWidth: { xs: "100%", sm: "100px" },
                      maxWidth: { sm: "150px" },
                      marginLeft: { sm: "8px" },
                      textTransform: "none",
                      fontSize: "0.75rem",
                      borderColor: "#f44336",
                      color: "#fff",
                      backgroundColor: "#f44336",
                      transition: "all 0.3s ease-in-out",
                      "&:hover": {
                        borderColor: "#d32f2f",
                        backgroundColor: "#d32f2f",
                        transform: "scale(1.02)",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#f5f5f5",
                        color: "#999",
                        borderColor: "#e0e0e0",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </>
  );
};

export default CostObjectEdit;